import { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import { getBackgroundMusicStreamingUrl } from '@/hooks/useBackgroundMusic';

export const useBackgroundAudio = () => {
  const [backgroundAudioUrl, setBackgroundAudioUrl] = useState<string | null>(null);
  const [isBackgroundReady, setIsBackgroundReady] = useState(false);
  const [backgroundVolume, setBackgroundVolume] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('bg_audio_volume');
      const parsed = saved ? parseFloat(saved) : NaN;
      return !isNaN(parsed) ? Math.min(1, Math.max(0, parsed)) : 0.5;
    } catch {
      return 0.5;
    }
  }); // Default 50% volume (persisted)
  const [isBackgroundPlaying, setIsBackgroundPlaying] = useState(false);
  const backgroundAudioRef = useRef<HTMLAudioElement | null>(null);
  const hasAnnouncedReadyRef = useRef(false);
  const autoStopTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Load background audio - simplified to just set the URL
  // The audio element will handle loading, errors, and readiness
  const loadBackgroundAudio = useCallback(async (musicFileName?: string) => {
    const fileName = musicFileName || 'universal_background.mp3';
    console.log('🎵 Loading background audio file:', fileName);

    try {
      const url = await getBackgroundMusicStreamingUrl(fileName);
      console.log('📁 Background audio streaming URL obtained:', url.substring(0, 50) + '...');

      setBackgroundAudioUrl(url);
      hasAnnouncedReadyRef.current = false; // Reset ready announcement for new file
    } catch (error) {
      console.error('Failed to load background audio:', error);
      toast({
        title: 'Background music error',
        description: 'Failed to load background music. Please try again.',
        variant: 'destructive'
      });
    }
  }, []);

  // Ensure background audio is loaded with the specified music file
  const ensureBackgroundAudioLoaded = useCallback(async (_categoryId?: string, musicFileName?: string) => {
    const targetFileName = musicFileName || 'universal_background.mp3';

    // Extract filename from URL (might include query params with token)
    const currentFileName = backgroundAudioUrl
      ? backgroundAudioUrl.split('/').pop()?.split('?')[0]
      : null;

    console.log('🎵 ensureBackgroundAudioLoaded - current:', currentFileName, 'target:', targetFileName);

    // Load if no background audio OR if requesting different music file
    if (!backgroundAudioUrl || currentFileName !== targetFileName) {
      console.log(`🔄 Loading background audio: ${targetFileName}`);
      await loadBackgroundAudio(musicFileName);
    } else {
      console.log('✅ Background audio already loaded:', currentFileName);
    }
  }, [backgroundAudioUrl, loadBackgroundAudio]);

  // Initialize background audio element - recreate when URL changes
  useEffect(() => {
    console.log('🎧 Background audio effect triggered:', {
      backgroundAudioUrl,
      hasExistingAudioRef: !!backgroundAudioRef.current
    });
    
    if (backgroundAudioUrl) {
      // Clean up existing audio element if it exists
      if (backgroundAudioRef.current) {
        console.log('🧹 Cleaning up existing background audio element...');
        backgroundAudioRef.current.pause();
        backgroundAudioRef.current.src = '';
        backgroundAudioRef.current = null;
        setIsBackgroundReady(false);
      }
      
      console.log('🔧 Creating new background audio element for:', backgroundAudioUrl);
      const audio = new Audio(backgroundAudioUrl);
      audio.crossOrigin = "use-credentials"; // Include auth cookies for authenticated endpoint
      audio.loop = true;
      audio.volume = backgroundVolume;
      audio.preload = 'auto';
      
      // Add event listeners for debugging and readiness
      audio.addEventListener('loadstart', () => console.log('🔄 Background audio loading started'));
      audio.addEventListener('canplaythrough', () => {
        console.log('✅ Background audio can play through');
        setIsBackgroundReady(true);
        if (!hasAnnouncedReadyRef.current) {
          const fileName = backgroundAudioUrl.split('/').pop() || 'background music';
          toast({ 
            title: 'Background music ready', 
            description: `"${fileName}" is loaded and ready to play.` 
          });
          hasAnnouncedReadyRef.current = true;
        }
      });
      
      // Track playback state
      audio.addEventListener('play', () => setIsBackgroundPlaying(true));
      audio.addEventListener('pause', () => setIsBackgroundPlaying(false));
      audio.addEventListener('ended', () => setIsBackgroundPlaying(false));
      audio.addEventListener('error', (e) => {
        console.error('❌ Background audio error:', e);
        setIsBackgroundReady(false);
        const fileName = backgroundAudioUrl.split('/').pop() || 'background music';
        toast({ 
          title: 'Background audio error', 
          description: `Could not load "${fileName}". Check if the file exists in storage.`,
          variant: "destructive"
        });
      });
      
      backgroundAudioRef.current = audio;
      console.log('✅ Background audio element created for:', backgroundAudioUrl);
    }
  }, [backgroundAudioUrl, backgroundVolume]); // Include backgroundVolume to update volume when URL changes

  // Update background volume
  const updateBackgroundVolume = useCallback((volume: number) => {
    const clamped = Math.min(1, Math.max(0, volume));
    setBackgroundVolume(clamped);
    try {
      localStorage.setItem('bg_audio_volume', clamped.toString());
    } catch {}
    if (backgroundAudioRef.current) {
      backgroundAudioRef.current.volume = clamped;
    }
  }, []);

  // Play background audio
  const playBackgroundAudio = useCallback(async () => {
    console.log('🎵 playBackgroundAudio called', {
      hasAudioRef: !!backgroundAudioRef.current,
      audioSrc: backgroundAudioRef.current?.src,
      audioReadyState: backgroundAudioRef.current?.readyState
    });
    
    if (backgroundAudioRef.current) {
      try {
        console.log('▶️ Attempting to play background audio...');
        await backgroundAudioRef.current.play();
        console.log('✅ Background audio started playing');
      } catch (error) {
        console.error('❌ Failed to play background audio:', error);
      }
    } else {
      console.error('❌ No background audio ref available');
      toast({ title: 'Preparing background music', description: 'Please wait a moment and try again.' });
    }
  }, []);

  // Pause background audio
  const pauseBackgroundAudio = useCallback(() => {
    if (backgroundAudioRef.current) {
      backgroundAudioRef.current.pause();
    }
  }, []);

  // Start auto-stop timer
  const startAutoStopTimer = useCallback((delaySeconds: number = 20) => {
    // Cancel any existing timer
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
    }
    
    console.log(`🔄 Starting auto-stop timer: ${delaySeconds}s`);
    
    autoStopTimerRef.current = setTimeout(() => {
      console.log('⏰ Auto-stopping background music after session end');
      
      if (backgroundAudioRef.current && isBackgroundPlaying) {
        pauseBackgroundAudio();
        
        // Show notification
        toast({
          title: 'Background music stopped',
          description: `Music automatically stopped ${delaySeconds}s after session ended.`,
        });
      }
      
      autoStopTimerRef.current = null;
    }, delaySeconds * 1000);
  }, [isBackgroundPlaying, pauseBackgroundAudio]);

  // Cancel auto-stop timer
  const cancelAutoStopTimer = useCallback(() => {
    if (autoStopTimerRef.current) {
      console.log('❌ Canceling auto-stop timer');
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
  }, []);

  // Reset background audio
  const resetBackgroundAudio = useCallback(() => {
    // Cancel any pending auto-stop timer
    cancelAutoStopTimer();
    
    if (backgroundAudioRef.current) {
      backgroundAudioRef.current.pause();
      backgroundAudioRef.current.currentTime = 0;
    }
    setBackgroundAudioUrl(null);
    setIsBackgroundReady(false);
    backgroundAudioRef.current = null;
  }, [cancelAutoStopTimer]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      cancelAutoStopTimer();
    };
  }, [cancelAutoStopTimer]);

  return {
    backgroundAudioUrl,
    isBackgroundReady,
    isBackgroundPlaying,
    backgroundVolume,
    loadBackgroundAudio,
    ensureBackgroundAudioLoaded,
    updateBackgroundVolume,
    playBackgroundAudio,
    pauseBackgroundAudio,
    resetBackgroundAudio,
    startAutoStopTimer,
    cancelAutoStopTimer
  };
};